// Facility staff management — 1:1 port of the legacy Next.js component
// (src/components/account/facility-staff-client.tsx + its
// facility-staff-page-client.tsx wrapper). Convention translations applied:
//   - useTranslations → Lingui (<Trans> / useLingui t``)
//   - Dialog → ResponsiveSheet (per CLAUDE.md mandate)
//   - raw fetch → apiFetch (@pontimed/api-client) + react-query
//   - raw palette classes → brand tokens (Badge variants + *-soft families)
//   - toasts follow the saving → saved/error pattern (showToast)
//
// Endpoints:
//   list     GET    /api/account/facility/:facilityId/hr/staff?all=1
//            (READ relaxed to any team member — mirrors legacy list gate)
//   branches GET    /api/account/facility/:facilityId/branches
//            (manager-gated → optional; 403 just hides the branch filter)
//   invite   POST   /api/account/facility-staff
//   role     PATCH  /api/account/facility-staff  { facilityId, staffId, role }
//   resend   PATCH  /api/account/facility-staff  { facilityId, staffId, resend }
//   remove   DELETE /api/account/facility-staff?facilityId=&staffId=
//
// Deviations from legacy (documented):
//   - Pending list is `!acceptedAt` (legacy `!acceptedAt && isActive` never
//     matched fresh invites, which are created with isActive=false — they
//     were invisible in BOTH sections). Team list is `acceptedAt` set; the
//     status badge still shows Inactive for deactivated members.
//   - Branch scoping reads `branchFacilityId` (the actual Prisma field);
//     legacy read a non-existent `branchId` so its branch filter matched
//     nothing.
//   - Caller role comes from the fetched list (own row) — the apps/api JWT
//     has no facilityTeams claim.

import { useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useSession } from "@pontimed/auth-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, m } from "framer-motion";
import {
  Building2,
  CalendarCheck,
  ChevronDown,
  ClipboardList,
  Clock,
  Crown,
  Filter,
  Loader2,
  Mail,
  MapPin,
  MoreVertical,
  Phone,
  Receipt,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";

import {
  FacilityStateCard,
  facilityErrorKind,
} from "@/components/account/facility/facility-state-card";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FilterChip } from "@/components/ui/filter-chip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { showToast } from "@/lib/ui/toast";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FacilityStaffRole =
  | "owner"
  | "director"
  | "facility_manager"
  | "admin"
  | "receptionist"
  | "billing";

interface StaffMember {
  id: string;
  facilityId: string;
  userId: string;
  role: FacilityStaffRole;
  branchFacilityId: string | null;
  invitedAt: string;
  acceptedAt: string | null;
  isActive: boolean;
  invitedByUserId: string | null;
  user: {
    id: string;
    email: string | null;
    phone: string | null;
    profile?: { firstName: string; lastName: string } | null;
  };
}

interface Branch {
  id: string;
  name: string;
}

interface BranchApiRow {
  id: string;
  name: Record<string, string> | null;
  branchName?: string | null;
}

// ---------------------------------------------------------------------------
// Role metadata — brand tokens only (legacy used raw amber/indigo/teal/…)
// ---------------------------------------------------------------------------

type BadgeVariant =
  | "default"
  | "secondary"
  | "success"
  | "warning"
  | "destructive"
  | "accent"
  | "info";

const ROLE_META: Record<
  FacilityStaffRole,
  { icon: typeof Crown; color: string; bg: string; badge: BadgeVariant }
> = {
  owner: { icon: Crown, color: "text-warning-strong", bg: "bg-warning-soft", badge: "warning" },
  director: {
    icon: Building2,
    color: "text-accent-strong",
    bg: "bg-accent-soft",
    badge: "accent",
  },
  facility_manager: {
    icon: MapPin,
    color: "text-primary",
    bg: "bg-primary-soft",
    badge: "default",
  },
  admin: { icon: ClipboardList, color: "text-info-strong", bg: "bg-info-soft", badge: "info" },
  receptionist: {
    icon: CalendarCheck,
    color: "text-success-strong",
    bg: "bg-success-soft",
    badge: "success",
  },
  billing: {
    icon: Receipt,
    color: "text-secondary-foreground",
    bg: "bg-secondary",
    badge: "secondary",
  },
};

// Mirrors legacy src/lib/permissions.ts FACILITY_ROLE_HIERARCHY (and the
// inline copy in apps/api/src/routes/account-facility-staff-invites.ts).
const FACILITY_ROLE_HIERARCHY: FacilityStaffRole[] = [
  "receptionist",
  "billing",
  "admin",
  "facility_manager",
  "director",
  "owner",
];

/** Translated facility-staff role (shared with the HR screens). */
export function roleLabel(role: string): React.ReactNode {
  switch (role) {
    case "owner":
      return <Trans>Owner</Trans>;
    case "director":
      return <Trans>Director</Trans>;
    case "facility_manager":
      return <Trans>Facility manager</Trans>;
    case "admin":
      return <Trans>Admin</Trans>;
    case "receptionist":
      return <Trans>Receptionist</Trans>;
    case "billing":
      return <Trans>Billing</Trans>;
    default:
      return role;
  }
}

// Values verbatim from legacy en.json facility.staff.*Desc.
function roleDescription(role: FacilityStaffRole): React.ReactNode {
  switch (role) {
    case "owner":
      return <Trans>Full control, cannot be removed</Trans>;
    case "director":
      return <Trans>Strategic access, all locations</Trans>;
    case "facility_manager":
      return <Trans>Full control on one location</Trans>;
    case "admin":
      return <Trans>Daily operations</Trans>;
    case "receptionist":
      return <Trans>Appointments only</Trans>;
    case "billing":
      return <Trans>Financial access only</Trans>;
  }
}

// ---------------------------------------------------------------------------
// Helpers (verbatim from legacy)
// ---------------------------------------------------------------------------

function getDisplayName(user: StaffMember["user"]): string {
  if (user.profile) return `${user.profile.firstName} ${user.profile.lastName}`;
  return user.email || user.phone || "—";
}

function getContact(user: StaffMember["user"]): { type: "email" | "phone"; value: string } | null {
  if (user.email && !user.email.endsWith("@phone.pontimed.app")) {
    return { type: "email", value: user.email };
  }
  if (user.phone) return { type: "phone", value: user.phone };
  return null;
}

function canManageRole(myRole: FacilityStaffRole, targetRole: FacilityStaffRole): boolean {
  const myLevel = FACILITY_ROLE_HIERARCHY.indexOf(myRole);
  const targetLevel = FACILITY_ROLE_HIERARCHY.indexOf(targetRole);
  return myLevel > targetLevel;
}

function getAssignableRoles(myRole: FacilityStaffRole, isOwner: boolean): FacilityStaffRole[] {
  return FACILITY_ROLE_HIERARCHY.filter((r) => {
    if (r === "owner") return false; // owner is never assignable
    if (isOwner) return true; // owner can assign everything except owner
    return canManageRole(myRole, r);
  });
}

// Selected/unselected chip classes — brand tokens (legacy used teal-*).
const CHIP_SELECTED = "border-primary bg-primary-soft text-primary";
const CHIP_IDLE = "border-border text-muted-foreground hover:bg-muted";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FacilityStaffClient({ facilityId }: { facilityId: string }) {
  const { t, i18n } = useLingui();
  const { user: sessionUser } = useSession();
  const queryClient = useQueryClient();

  const staffKey = useMemo(
    () => ["api", `/api/account/facility/${facilityId}/hr/staff`, "all"] as const,
    [facilityId],
  );

  const staffQ = useQuery({
    queryKey: staffKey,
    queryFn: () =>
      apiFetch.get<{ staff: StaffMember[] }>(`/api/account/facility/${facilityId}/hr/staff?all=1`),
    retry: false,
  });

  // Branch list is manager-gated on the API — a 403 for receptionist/billing
  // viewers simply hides the branch filter (legacy fetched it in the page
  // wrapper where only managers reached the query).
  const branchesQ = useQuery({
    queryKey: ["api", `/api/account/facility/${facilityId}/branches`] as const,
    queryFn: async (): Promise<Branch[]> => {
      try {
        const json = await apiFetch.get<{ branches: BranchApiRow[] }>(
          `/api/account/facility/${facilityId}/branches`,
        );
        const locale = i18n.locale;
        return (json.branches ?? []).map((b) => ({
          id: b.id,
          name: b.name?.[locale] ?? b.name?.en ?? b.name?.ro ?? b.branchName ?? "—",
        }));
      } catch {
        return [];
      }
    },
    retry: false,
  });

  const staff = useMemo(() => staffQ.data?.staff ?? [], [staffQ.data]);
  const branches = branchesQ.data ?? [];

  const refetchStaff = () => queryClient.invalidateQueries({ queryKey: staffKey });

  // Invite sheet
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteContact, setInviteContact] = useState("");
  const [inviteRole, setInviteRole] = useState<FacilityStaffRole>("receptionist");
  const [inviteBranch, setInviteBranch] = useState<string>("");
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState("");

  // Change role sheet
  const [changeRoleTarget, setChangeRoleTarget] = useState<StaffMember | null>(null);
  const [newRole, setNewRole] = useState<FacilityStaffRole>("receptionist");
  const [changingRole, setChangingRole] = useState(false);

  // Remove sheet
  const [removeTarget, setRemoveTarget] = useState<StaffMember | null>(null);
  const [removing, setRemoving] = useState(false);

  // Branch filter
  const [filterBranch, setFilterBranch] = useState<string>("all");

  // ----- Derived data -----
  // Caller's role resolved from own row in the list (see header comment).
  const currentUserRole = (staff.find((s) => s.userId === sessionUser?.userId)?.role ??
    "") as FacilityStaffRole;
  const isOwner = currentUserRole === "owner";

  const activeStaff = staff.filter((s) => s.acceptedAt);
  const pendingStaff = staff.filter((s) => !s.acceptedAt);
  const assignableRoles = currentUserRole ? getAssignableRoles(currentUserRole, isOwner) : [];

  const filteredActiveStaff =
    filterBranch === "all"
      ? activeStaff
      : activeStaff.filter(
          (s) => s.branchFacilityId === filterBranch || s.branchFacilityId === null,
        );

  // ----- Handlers -----

  const handleInvite = async () => {
    if (!inviteContact.trim()) return;
    setInviting(true);
    setInviteError("");
    const toast = showToast(t`Sending…`, "loading");
    try {
      await apiFetch.post("/api/account/facility-staff", {
        facilityId,
        emailOrPhone: inviteContact.trim(),
        role: inviteRole,
        branchId: inviteBranch || null,
      });
      toast.update(t`Invitation sent.`, "success");
      setInviteOpen(false);
      setInviteContact("");
      setInviteRole("receptionist");
      setInviteBranch("");
      await refetchStaff();
    } catch (err) {
      const apiMsg =
        err instanceof ApiError && typeof err.body === "object" && err.body
          ? (err.body as { error?: string }).error
          : undefined;
      const message =
        apiMsg === "ALREADY_STAFF"
          ? t`This person is already a team member.`
          : t`Could not send invitation. Try again.`;
      setInviteError(message);
      toast.update(message, "error");
    } finally {
      setInviting(false);
    }
  };

  const handleChangeRole = async () => {
    if (!changeRoleTarget) return;
    setChangingRole(true);
    const toast = showToast(t`Saving…`, "loading");
    try {
      await apiFetch.patch("/api/account/facility-staff", {
        facilityId,
        staffId: changeRoleTarget.id,
        role: newRole,
      });
      toast.update(t`Role changed.`, "success");
      setChangeRoleTarget(null);
      await refetchStaff();
    } catch {
      toast.update(t`Could not change role. Try again.`, "error");
    } finally {
      setChangingRole(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    const toast = showToast(t`Saving…`, "loading");
    try {
      await apiFetch.delete(
        `/api/account/facility-staff?facilityId=${encodeURIComponent(facilityId)}&staffId=${encodeURIComponent(removeTarget.id)}`,
      );
      toast.update(t`Member removed.`, "success");
      setRemoveTarget(null);
      await refetchStaff();
    } catch {
      toast.update(t`Could not remove member. Try again.`, "error");
    } finally {
      setRemoving(false);
    }
  };

  const handleResend = async (staffId: string) => {
    const toast = showToast(t`Sending…`, "loading");
    try {
      await apiFetch.patch("/api/account/facility-staff", {
        facilityId,
        staffId,
        resend: true,
      });
      toast.update(t`Invitation re-sent.`, "success");
    } catch {
      toast.update(t`Could not send invitation. Try again.`, "error");
    }
  };

  const handleCancelInvite = async (staffId: string) => {
    const toast = showToast(t`Saving…`, "loading");
    try {
      await apiFetch.delete(
        `/api/account/facility-staff?facilityId=${encodeURIComponent(facilityId)}&staffId=${encodeURIComponent(staffId)}`,
      );
      toast.update(t`Invitation cancelled.`, "success");
      await refetchStaff();
    } catch {
      toast.update(t`Could not remove member. Try again.`, "error");
    }
  };

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString(i18n.locale);

  // ----- Loading -----
  if (staffQ.isLoading) return <StaffSkeleton />;

  // ----- No access -----
  if (staffQ.isError) {
    return (
      <FacilityStateCard
        kind={facilityErrorKind(staffQ.error)}
        facilityId={facilityId}
        onRetry={() => void staffQ.refetch()}
        retrying={staffQ.isFetching}
      />
    );
  }

  return (
    <div className="space-y-8">
      {/* ===== HEADER ===== */}
      <PageHeader
        title={<Trans>Staff</Trans>}
        description={<Trans>Manage your team and permissions</Trans>}
        actions={
          assignableRoles.length > 0 ? (
            <Button size="touch" onClick={() => setInviteOpen(true)}>
              <UserPlus className="size-4" aria-hidden />
              <span className="hidden sm:inline">
                <Trans>Invite member</Trans>
              </span>
              <span className="sm:hidden">
                <Trans>Invite</Trans>
              </span>
            </Button>
          ) : undefined
        }
      />

      {/* ===== BRANCH FILTER ===== */}
      {branches.length > 0 && (
        <div className="flex items-center gap-2">
          <Filter className="text-muted-foreground h-4 w-4 shrink-0" />
          <div className="flex flex-wrap gap-2">
            <FilterChip active={filterBranch === "all"} onClick={() => setFilterBranch("all")}>
              <Trans>All branches</Trans>
            </FilterChip>
            {branches.map((b) => (
              <FilterChip
                key={b.id}
                active={filterBranch === b.id}
                onClick={() => setFilterBranch(b.id)}
              >
                {b.name}
              </FilterChip>
            ))}
          </div>
        </div>
      )}

      {/* ===== TEAM MEMBERS ===== */}
      <div>
        <h2 className="text-foreground mb-4 text-lg font-semibold">
          <Trans>Team members</Trans>
        </h2>

        {filteredActiveStaff.length === 0 ? (
          <div className="border-border bg-card rounded-xl border p-5">
            <div className="border-border flex min-h-[250px] flex-col items-center justify-center rounded-lg border-2 border-dashed p-12">
              <div className="bg-primary-soft rounded-xl p-4">
                <Users className="text-primary h-8 w-8" />
              </div>
              <h3 className="text-foreground mt-4 text-base font-semibold">
                <Trans>Invite your team</Trans>
              </h3>
              <p className="text-muted-foreground mt-2 max-w-sm text-center text-sm">
                <Trans>
                  Add team members and assign roles to efficiently manage your clinic. Each role has
                  specific permissions.
                </Trans>
              </p>
              {assignableRoles.length > 0 && (
                <Button size="touch" onClick={() => setInviteOpen(true)} className="mt-6">
                  <UserPlus className="size-4" aria-hidden />
                  <Trans>Invite first member</Trans>
                </Button>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="border-border bg-card hidden overflow-hidden rounded-xl border md:block">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-border bg-muted/50 text-muted-foreground border-b text-left text-xs font-medium tracking-wider uppercase">
                      <th className="px-5 py-3">
                        <Trans>Member</Trans>
                      </th>
                      <th className="px-5 py-3">
                        <Trans>Contact</Trans>
                      </th>
                      <th className="px-5 py-3">
                        <Trans>Role</Trans>
                      </th>
                      <th className="hidden px-5 py-3 lg:table-cell">
                        <Trans>Branch</Trans>
                      </th>
                      <th className="hidden px-5 py-3 lg:table-cell">
                        <Trans>Status</Trans>
                      </th>
                      <th className="px-5 py-3 text-right">
                        <Trans>Actions</Trans>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {filteredActiveStaff.map((s) => (
                        <StaffRow
                          key={s.id}
                          staff={s}
                          currentUserRole={currentUserRole}
                          branches={branches}
                          onChangeRole={() => {
                            setChangeRoleTarget(s);
                            setNewRole(s.role);
                          }}
                          onRemove={() => setRemoveTarget(s)}
                        />
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile cards */}
            <div className="space-y-3 md:hidden">
              <AnimatePresence>
                {filteredActiveStaff.map((s) => (
                  <StaffCard
                    key={s.id}
                    staff={s}
                    currentUserRole={currentUserRole}
                    branches={branches}
                    onChangeRole={() => {
                      setChangeRoleTarget(s);
                      setNewRole(s.role);
                    }}
                    onRemove={() => setRemoveTarget(s)}
                  />
                ))}
              </AnimatePresence>
            </div>
          </>
        )}
      </div>

      {/* ===== ROLES OVERVIEW =====
          Below the team (it used to push the list off the first screen on a
          phone) and collapsed: a reference, not the page's content. */}
      <details className="group border-border bg-card rounded-xl border">
        <summary className="text-foreground flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold [&::-webkit-details-marker]:hidden">
          <Trans>What can each role do?</Trans>
          <ChevronDown
            className="text-muted-foreground size-4 shrink-0 transition-transform group-open:rotate-180"
            aria-hidden
          />
        </summary>
        <ul className="divide-border border-border divide-y border-t">
          {(Object.keys(ROLE_META) as FacilityStaffRole[]).map((role) => {
            const meta = ROLE_META[role];
            const Icon = meta.icon;
            const count = activeStaff.filter((s) => s.role === role).length;
            return (
              <li key={role} className="flex items-start gap-3 px-4 py-3">
                <div className={`rounded-lg p-2 ${meta.bg}`}>
                  <Icon className={`h-4 w-4 ${meta.color}`} aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-foreground text-sm font-semibold">{roleLabel(role)}</h3>
                    {count > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {count}
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground mt-0.5 text-sm">{roleDescription(role)}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </details>

      {/* ===== PENDING INVITATIONS ===== */}
      <div>
        <h2 className="text-foreground mb-4 text-lg font-semibold">
          <Trans>Pending invitations</Trans>
        </h2>

        {pendingStaff.length === 0 ? (
          <div className="border-border flex items-center justify-center rounded-xl border-2 border-dashed p-8">
            <p className="text-muted-foreground text-sm">
              <Trans>No pending invitations</Trans>
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence>
              {pendingStaff.map((s) => {
                const contact = getContact(s.user);
                return (
                  <m.div
                    key={s.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                    className="border-border bg-card flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-muted flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
                        <Clock className="text-muted-foreground h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-foreground text-sm font-medium">
                          {contact?.value || getDisplayName(s.user)}
                        </p>
                        <div className="text-muted-foreground flex items-center gap-2 text-xs">
                          <Badge variant={ROLE_META[s.role]?.badge ?? "info"}>
                            {roleLabel(s.role)}
                          </Badge>
                          <span>
                            <Trans>Invited on</Trans> {formatDate(s.invitedAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                    {assignableRoles.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="touch" onClick={() => handleResend(s.id)}>
                          <RefreshCw className="h-3.5 w-3.5" />
                          <Trans>Resend invite</Trans>
                        </Button>
                        <Button
                          variant="ghost"
                          size="touch"
                          onClick={() => handleCancelInvite(s.id)}
                          className="text-destructive hover:bg-destructive-soft"
                        >
                          <X className="h-3.5 w-3.5" />
                          <Trans>Cancel invite</Trans>
                        </Button>
                      </div>
                    )}
                  </m.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* ===== INVITE SHEET ===== */}
      <ResponsiveSheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title={<Trans>Invite member</Trans>}
        description={<Trans>Invite a colleague to join the clinic team</Trans>}
        width="md"
        closeLabel={t`Close`}
        footer={
          <div className="flex justify-end gap-2">
            <Button size="touch" variant="outline" onClick={() => setInviteOpen(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              size="touch"
              onClick={handleInvite}
              disabled={inviting || !inviteContact.trim()}
              className="gap-2"
            >
              {inviting && <Loader2 className="h-4 w-4 animate-spin" />}
              <Trans>Send invite</Trans>
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Contact input */}
          <div>
            <label
              htmlFor="invite-contact"
              className="text-muted-foreground mb-1.5 block text-xs font-medium"
            >
              <Trans>Email or phone</Trans>
            </label>
            <Input
              id="invite-contact"
              value={inviteContact}
              onChange={(e) => {
                setInviteContact(e.target.value);
                setInviteError("");
              }}
              placeholder={t`name@example.com or +40...`}
            />
          </div>

          {/* Role selector */}
          <div>
            <span
              id="invite-role-label"
              className="text-muted-foreground mb-1.5 block text-xs font-medium"
            >
              <Trans>Role</Trans>
            </span>
            <RoleRadioList
              labelledBy="invite-role-label"
              roles={assignableRoles}
              value={inviteRole}
              onChange={setInviteRole}
            />
          </div>

          {/* Branch scoping */}
          {branches.length > 0 && (
            <div>
              <p
                id="invite-branch-label"
                className="text-muted-foreground mb-1.5 block text-xs font-medium"
              >
                <Trans>Branch access</Trans>
              </p>
              <div
                className="grid grid-cols-1 gap-2"
                role="group"
                aria-labelledby="invite-branch-label"
              >
                <button
                  type="button"
                  onClick={() => setInviteBranch("")}
                  className={`min-h-[44px] rounded-lg border px-3 py-2.5 text-xs font-medium transition-colors ${
                    !inviteBranch ? CHIP_SELECTED : CHIP_IDLE
                  }`}
                >
                  <Trans>All branches</Trans>
                </button>
                {branches.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setInviteBranch(b.id)}
                    className={`min-h-[44px] rounded-lg border px-3 py-2.5 text-xs font-medium transition-colors ${
                      inviteBranch === b.id ? CHIP_SELECTED : CHIP_IDLE
                    }`}
                  >
                    {b.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {inviteError && <p className="text-destructive text-sm">{inviteError}</p>}
        </div>
      </ResponsiveSheet>

      {/* ===== CHANGE ROLE SHEET ===== */}
      <ResponsiveSheet
        open={!!changeRoleTarget}
        onClose={() => setChangeRoleTarget(null)}
        title={<Trans>Change role</Trans>}
        description={
          changeRoleTarget ? (
            <Trans>Change role for {getDisplayName(changeRoleTarget.user)}</Trans>
          ) : null
        }
        width="sm"
        closeLabel={t`Close`}
        footer={
          <div className="flex justify-end gap-2">
            <Button size="touch" variant="outline" onClick={() => setChangeRoleTarget(null)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              size="touch"
              onClick={handleChangeRole}
              disabled={changingRole || newRole === changeRoleTarget?.role}
              className="gap-2"
            >
              {changingRole && <Loader2 className="h-4 w-4 animate-spin" />}
              <Trans>Save</Trans>
            </Button>
          </div>
        }
      >
        <div>
          <RoleRadioList
            ariaLabel={t`Role`}
            roles={assignableRoles}
            value={newRole}
            onChange={setNewRole}
          />
        </div>
      </ResponsiveSheet>

      {/* ===== REMOVE SHEET ===== */}
      <ResponsiveSheet
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        title={<Trans>Remove member</Trans>}
        description={
          removeTarget ? (
            <Trans>
              Are you sure you want to remove {getDisplayName(removeTarget.user)}? This action
              cannot be undone.
            </Trans>
          ) : null
        }
        width="sm"
        closeLabel={t`Close`}
        footer={
          <div className="flex justify-end gap-2">
            <Button size="touch" variant="outline" onClick={() => setRemoveTarget(null)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              size="touch"
              variant="destructive"
              onClick={handleRemove}
              disabled={removing}
              className="gap-2"
            >
              {removing && <Loader2 className="h-4 w-4 animate-spin" />}
              <Trans>Remove member</Trans>
            </Button>
          </div>
        }
      >
        <div aria-hidden className="h-1" />
      </ResponsiveSheet>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// Single-column role picker for the Invite + Change-role sheets: each option
// carries its description, which is where it helps the decision.
function RoleRadioList({
  roles,
  value,
  onChange,
  labelledBy,
  ariaLabel,
}: {
  roles: FacilityStaffRole[];
  value: FacilityStaffRole;
  onChange: (role: FacilityStaffRole) => void;
  labelledBy?: string;
  ariaLabel?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-labelledby={labelledBy}
      aria-label={ariaLabel}
      className="space-y-2"
    >
      {roles.map((r) => {
        const selected = value === r;
        return (
          <button
            key={r}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(r)}
            className={`flex min-h-11 w-full flex-col items-start rounded-lg border px-3 py-2.5 text-left transition-colors ${
              selected ? CHIP_SELECTED : CHIP_IDLE
            }`}
          >
            <span className={`text-sm font-medium ${selected ? "" : "text-foreground"}`}>
              {roleLabel(r)}
            </span>
            <span className="text-muted-foreground text-xs">{roleDescription(r)}</span>
          </button>
        );
      })}
    </div>
  );
}

function StaffRow({
  staff,
  currentUserRole,
  branches,
  onChangeRole,
  onRemove,
}: {
  staff: StaffMember;
  currentUserRole: FacilityStaffRole;
  branches: Branch[];
  onChangeRole: () => void;
  onRemove: () => void;
}) {
  const contact = getContact(staff.user);
  const canManage = currentUserRole ? canManageRole(currentUserRole, staff.role) : false;
  const branch = branches.find((b) => b.id === staff.branchFacilityId);

  return (
    <m.tr
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      className="border-border border-b last:border-0"
    >
      <td className="px-5 py-4">
        <p className="text-foreground text-sm font-medium">{getDisplayName(staff.user)}</p>
      </td>
      <td className="px-5 py-4">
        {contact && (
          <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
            {contact.type === "email" ? (
              <Mail className="h-3.5 w-3.5" />
            ) : (
              <Phone className="h-3.5 w-3.5" />
            )}
            {contact.value}
          </div>
        )}
      </td>
      <td className="px-5 py-4">
        <Badge variant={ROLE_META[staff.role]?.badge ?? "info"}>{roleLabel(staff.role)}</Badge>
      </td>
      <td className="hidden px-5 py-4 lg:table-cell">
        <span className="text-muted-foreground text-sm">
          {branch ? branch.name : <Trans>All branches</Trans>}
        </span>
      </td>
      <td className="hidden px-5 py-4 lg:table-cell">
        <StatusBadge accepted={!!staff.acceptedAt} active={staff.isActive} />
      </td>
      <td className="px-5 py-4 text-right">
        {canManage && staff.role !== "owner" && (
          <StaffActions onChangeRole={onChangeRole} onRemove={onRemove} size="sm" />
        )}
      </td>
    </m.tr>
  );
}

function StaffCard({
  staff,
  currentUserRole,
  branches,
  onChangeRole,
  onRemove,
}: {
  staff: StaffMember;
  currentUserRole: FacilityStaffRole;
  branches: Branch[];
  onChangeRole: () => void;
  onRemove: () => void;
}) {
  const contact = getContact(staff.user);
  const canManage = currentUserRole ? canManageRole(currentUserRole, staff.role) : false;
  const branch = branches.find((b) => b.id === staff.branchFacilityId);

  return (
    <m.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      className="border-border bg-card rounded-xl border p-4"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-foreground truncate text-sm font-medium">
            {getDisplayName(staff.user)}
          </p>
          {contact && (
            <div className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-xs">
              {contact.type === "email" ? (
                <Mail className="h-3 w-3 shrink-0" />
              ) : (
                <Phone className="h-3 w-3 shrink-0" />
              )}
              <span className="truncate">{contact.value}</span>
            </div>
          )}
        </div>
        {canManage && staff.role !== "owner" && (
          <StaffActions onChangeRole={onChangeRole} onRemove={onRemove} size="lg" />
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Badge variant={ROLE_META[staff.role]?.badge ?? "info"}>{roleLabel(staff.role)}</Badge>
        <StatusBadge accepted={!!staff.acceptedAt} active={staff.isActive} />
        {branch && (
          <span className="bg-muted text-muted-foreground inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs">
            <MapPin className="h-3 w-3" />
            {branch.name}
          </span>
        )}
      </div>
    </m.div>
  );
}

// Popover-based action menu — apps/app has no DropdownMenu primitive (same
// pattern as provider-team-client.tsx MemberActions).
function StaffActions({
  onChangeRole,
  onRemove,
  size,
}: {
  onChangeRole: () => void;
  onRemove: () => void;
  size: "sm" | "lg";
}) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            size="sm"
            className={`text-muted-foreground p-0 ${size === "lg" ? "h-11 w-11" : "size-11 sm:pointer-fine:size-9"}`}
            aria-label={t`Actions`}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        }
      />
      <PopoverContent align="end" className="w-48 p-1">
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            onChangeRole();
          }}
          className="text-foreground hover:bg-muted flex min-h-11 w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm"
        >
          <ShieldCheck className="h-4 w-4" />
          <Trans>Change role</Trans>
        </button>
        <div className="bg-border my-1 h-px" />
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            onRemove();
          }}
          className="text-destructive hover:bg-destructive-soft flex min-h-11 w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm"
        >
          <Trash2 className="h-4 w-4" />
          <Trans>Remove member</Trans>
        </button>
      </PopoverContent>
    </Popover>
  );
}

function StatusBadge({ accepted, active }: { accepted: boolean; active: boolean }) {
  if (!active)
    return (
      <Badge variant="destructive">
        <Trans>Inactive</Trans>
      </Badge>
    );
  if (!accepted)
    return (
      <Badge variant="warning">
        <Trans>Pending</Trans>
      </Badge>
    );
  return (
    <Badge variant="success">
      <Trans>Active</Trans>
    </Badge>
  );
}

function StaffSkeleton() {
  return (
    <div className="space-y-8">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <div className="bg-muted h-7 w-40 animate-pulse rounded-lg" />
          <div className="bg-muted h-4 w-64 animate-pulse rounded-lg" />
        </div>
        <div className="bg-muted h-10 w-36 animate-pulse rounded-lg" />
      </div>
      {/* Roles skeleton */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="border-border bg-card h-20 animate-pulse rounded-xl border" />
        ))}
      </div>
      {/* Table skeleton */}
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="border-border bg-card h-16 animate-pulse rounded-xl border" />
        ))}
      </div>
    </div>
  );
}
